import { useMemo, useContext } from 'react';
import { useTheme } from 'foundations-theming-specialty/utils';
import { List } from 'immutable';
import { Palette, PaletteIds, DiscreteScheme, DiscretePatternScheme, InterpolateScheme } from 'reporting-data/relational/reports/palettes-records';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { DO_NOT_USE_DEPRECATED_PALETTES } from '../color/deprecatedPalettes';
import { gate } from 'hub-http/gates';
const TOKENIZED_PALETTES_GATE = gate('ReportingViz:TokenizedPalettes');
export const useReportingPalettes = () => {
  const theme = useTheme();
  const {
    gates
  } = useContext(UserInfoContext) || {
    gates: []
  };
  return useMemo(() => {
    if (!(gates !== null && gates !== void 0 && gates.includes(TOKENIZED_PALETTES_GATE))) {
      return DO_NOT_USE_DEPRECATED_PALETTES;
    }
    const getTrellisTokenHexValue = tokenKey => theme.color[tokenKey] || '#ffffff';
    const DefaultCategorical = Palette({
      id: PaletteIds.Categorical14,
      label: 'reporting-snowflake.colors.palettes.default-14',
      scheme: DiscreteScheme({
        colors: [{
          label: 'Orange',
          key: 'orange',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-orange')
        }, {
          label: 'Aqua',
          key: 'aqua',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-aqua')
        }, {
          label: 'Purple',
          key: 'purple',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-purple')
        }, {
          label: 'Yellow',
          key: 'yellow',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-yellow')
        }, {
          label: 'Pink',
          key: 'pink',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-pink')
        }, {
          label: 'Blue',
          key: 'blue',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-blue')
        }, {
          label: 'Green',
          key: 'green',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-green')
        }, {
          label: 'DARK_ORANGE',
          key: 'dark-orange',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-orange')
        }, {
          label: 'DARK_AQUA',
          key: 'dark-aqua',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-aqua')
        }, {
          label: 'DARK_PURPLE',
          key: 'dark-purple',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-purple')
        }, {
          label: 'DARK_YELLOW',
          key: 'dark-yellow',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-yellow')
        }, {
          label: 'DARK_PINK',
          key: 'dark-pink',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-pink')
        }, {
          label: 'DARK_BLUE',
          key: 'dark-blue',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-blue')
        }, {
          label: 'DARK_GREEN',
          key: 'dark-green',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-green')
        }]
      })
    });

    // Tokenized PatternFill14 palette using same tokens as DefaultCategorical
    const PatternFill14 = Palette({
      id: PaletteIds.PatternFill14,
      label: 'reporting-snowflake.colors.palettes.pattern-fill-14',
      scheme: DiscretePatternScheme({
        colors: [{
          label: 'Orange',
          key: 'orange',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-orange')
        }, {
          label: 'Aqua',
          key: 'aqua',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-aqua')
        }, {
          label: 'Purple',
          key: 'purple',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-purple')
        }, {
          label: 'Yellow',
          key: 'yellow',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-yellow')
        }, {
          label: 'Pink',
          key: 'pink',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-pink')
        }, {
          label: 'Blue',
          key: 'blue',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-blue')
        }, {
          label: 'Green',
          key: 'green',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-green')
        }, {
          label: 'DARK_ORANGE',
          key: 'dark-orange',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-orange')
        }, {
          label: 'DARK_AQUA',
          key: 'dark-aqua',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-aqua')
        }, {
          label: 'DARK_PURPLE',
          key: 'dark-purple',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-purple')
        }, {
          label: 'DARK_YELLOW',
          key: 'dark-yellow',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-yellow')
        }, {
          label: 'DARK_PINK',
          key: 'dark-pink',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-pink')
        }, {
          label: 'DARK_BLUE',
          key: 'dark-blue',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-blue')
        }, {
          label: 'DARK_GREEN',
          key: 'dark-green',
          value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-green')
        }],
        patterns: [
        // Pulled from Highcharts' default patterns
        'M 0 10 L 10 0 M -1 1 L 1 -1 M 9 11 L 11 9',
        // Line: /
        'M 0 0 L 5 10 L 10 0',
        //ZigZag Horizontal
        'M 3 3 L 8 3 L 8 8 L 3 8 Z',
        // Squares
        'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
        // Line: \
        'M 5 5 m -4 0 a 4 4 0 1 1 8 0 a 4 4 0 1 1 -8 0' // Circles
        ],
        dashStylePatterns: ['Solid', 'ShortDash', 'ShortDot', 'ShortDashDot', 'ShortDashDotDot', 'Dot', 'Dash', 'LongDash', 'DashDot', 'LongDashDot', 'LongDashDotDot']
      })
    });

    // Tokenized ShadesOf palettes using scale tokens - 2-point interpolation
    const ShadesOfAqua = Palette({
      id: PaletteIds.ShadesOfAqua,
      label: 'reporting-snowflake.colors.palettes.shades-of-aqua',
      scheme: InterpolateScheme({
        points: [{
          positionFraction: 0,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-aqua')
        }, {
          positionFraction: 1,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-aqua')
        }]
      })
    });
    const ShadesOfOrange = Palette({
      id: PaletteIds.ShadesOfOrange,
      label: 'reporting-snowflake.colors.palettes.shades-of-orange',
      scheme: InterpolateScheme({
        points: [{
          positionFraction: 0,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-orange')
        }, {
          positionFraction: 1,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-orange')
        }]
      })
    });
    const ShadesOfPurple = Palette({
      id: PaletteIds.ShadesOfPurple,
      label: 'reporting-snowflake.colors.palettes.shades-of-purple',
      scheme: InterpolateScheme({
        points: [{
          positionFraction: 0,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-purple')
        }, {
          positionFraction: 1,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-purple')
        }]
      })
    });
    const ShadesOfYellow = Palette({
      id: PaletteIds.ShadesOfYellow,
      label: 'reporting-snowflake.colors.palettes.shades-of-yellow',
      scheme: InterpolateScheme({
        points: [{
          positionFraction: 0,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-yellow')
        }, {
          positionFraction: 1,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-yellow')
        }]
      })
    });
    const ShadesOfPink = Palette({
      id: PaletteIds.ShadesOfPink,
      label: 'reporting-snowflake.colors.palettes.shades-of-pink',
      scheme: InterpolateScheme({
        points: [{
          positionFraction: 0,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-pink')
        }, {
          positionFraction: 1,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-pink')
        }]
      })
    });
    const ShadesOfBlue = Palette({
      id: PaletteIds.ShadesOfBlue,
      label: 'reporting-snowflake.colors.palettes.shades-of-blue',
      scheme: InterpolateScheme({
        points: [{
          positionFraction: 0,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-blue')
        }, {
          positionFraction: 1,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-blue')
        }]
      })
    });
    const ShadesOfGreen = Palette({
      id: PaletteIds.ShadesOfGreen,
      label: 'reporting-snowflake.colors.palettes.shades-of-green',
      scheme: InterpolateScheme({
        points: [{
          positionFraction: 0,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-green')
        }, {
          positionFraction: 1,
          value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-green')
        }]
      })
    });

    // Static palettes (not tokenized yet) - keep as hex values for backwards compatibility
    const Tokyo = Palette({
      id: PaletteIds.TokyoFive,
      label: 'reporting-snowflake.colors.palettes.tokyo-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#385315',
          value: '#385315'
        }, {
          label: '',
          key: '#A5B4B3',
          value: '#A5B4B3'
        }, {
          label: '',
          key: '#00627B',
          value: '#00627B'
        }, {
          label: '',
          key: '#AA4735',
          value: '#AA4735'
        }, {
          label: '',
          key: '#E8BAAF',
          value: '#E8BAAF'
        }]
      })
    });
    const Bogota = Palette({
      id: PaletteIds.BogotaFive,
      label: 'reporting-snowflake.colors.palettes.bogota-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#D7C1C2',
          value: '#D7C1C2'
        }, {
          label: '',
          key: '#2F4C5E',
          value: '#2F4C5E'
        }, {
          label: '',
          key: '#86343C',
          value: '#86343C'
        }, {
          label: '',
          key: '#B67D40',
          value: '#B67D40'
        }, {
          label: '',
          key: '#133C82',
          value: '#133C82'
        }]
      })
    });
    const Dublin = Palette({
      id: PaletteIds.DublinFive,
      label: 'reporting-snowflake.colors.palettes.dublin-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#43554F',
          value: '#43554F'
        }, {
          label: '',
          key: '#D6D8D4',
          value: '#D6D8D4'
        }, {
          label: '',
          key: '#A3BFBE',
          value: '#A3BFBE'
        }, {
          label: '',
          key: '#4EB39D',
          value: '#4EB39D'
        }, {
          label: '',
          key: '#C7E9C1',
          value: '#C7E9C1'
        }]
      })
    });
    const Cambridge = Palette({
      id: PaletteIds.CambridgeFive,
      label: 'reporting-snowflake.colors.palettes.cambridge-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#9C7475',
          value: '#9C7475'
        }, {
          label: '',
          key: '#968613',
          value: '#968613'
        }, {
          label: '',
          key: '#29464E',
          value: '#29464E'
        }, {
          label: '',
          key: '#4E1309',
          value: '#4E1309'
        }, {
          label: '',
          key: '#110235',
          value: '#110235'
        }]
      })
    });
    const Singapore = Palette({
      id: PaletteIds.SingaporeFive,
      label: 'reporting-snowflake.colors.palettes.singapore-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#8B9AF8',
          value: '#8B9AF8'
        }, {
          label: '',
          key: '#982B48',
          value: '#982B48'
        }, {
          label: '',
          key: '#CE7AB5',
          value: '#CE7AB5'
        }, {
          label: '',
          key: '#FEA575',
          value: '#FEA575'
        }, {
          label: '',
          key: '#2B5063',
          value: '#2B5063'
        }]
      })
    });
    const Sydney = Palette({
      id: PaletteIds.SydneyFive,
      label: 'reporting-snowflake.colors.palettes.sydney-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#2B8799',
          value: '#2B8799'
        }, {
          label: '',
          key: '#B96AA3',
          value: '#B96AA3'
        }, {
          label: '',
          key: '#62B9D4',
          value: '#62B9D4'
        }, {
          label: '',
          key: '#F5815F',
          value: '#F5815F'
        }, {
          label: '',
          key: '#5A5DA8',
          value: '#5A5DA8'
        }]
      })
    });
    const Berlin = Palette({
      id: PaletteIds.BerlinFive,
      label: 'reporting-snowflake.colors.palettes.berlin-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#4C82B0',
          value: '#4C82B0'
        }, {
          label: '',
          key: '#CA695D',
          value: '#CA695D'
        }, {
          label: '',
          key: '#D3AE84',
          value: '#D3AE84'
        }, {
          label: '',
          key: '#5EAADF',
          value: '#5EAADF'
        }, {
          label: '',
          key: '#4B850E',
          value: '#4B850E'
        }]
      })
    });
    const Paris = Palette({
      id: PaletteIds.ParisFive,
      label: 'reporting-snowflake.colors.palettes.paris-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#578A8C',
          value: '#578A8C'
        }, {
          label: '',
          key: '#79874A',
          value: '#79874A'
        }, {
          label: '',
          key: '#363D52',
          value: '#363D52'
        }, {
          label: '',
          key: '#DED5E6',
          value: '#DED5E6'
        }, {
          label: '',
          key: '#AFC1D9',
          value: '#AFC1D9'
        }]
      })
    });
    const SanFrancisco = Palette({
      id: PaletteIds.SanFranciscoFive,
      label: 'reporting-snowflake.colors.palettes.san-francisco-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#CB9E74',
          value: '#CB9E74'
        }, {
          label: '',
          key: '#5895B0',
          value: '#5895B0'
        }, {
          label: '',
          key: '#013E55',
          value: '#013E55'
        }, {
          label: '',
          key: '#991801',
          value: '#991801'
        }, {
          label: '',
          key: '#A5A5A0',
          value: '#A5A5A0'
        }]
      })
    });
    const Portsmouth = Palette({
      id: PaletteIds.PortsmouthFive,
      label: 'reporting-snowflake.colors.palettes.portsmouth-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#879E97',
          value: '#879E97'
        }, {
          label: '',
          key: '#D47469',
          value: '#D47469'
        }, {
          label: '',
          key: '#FCA170',
          value: '#FCA170'
        }, {
          label: '',
          key: '#217E9F',
          value: '#217E9F'
        }, {
          label: '',
          key: '#304B8D',
          value: '#304B8D'
        }]
      })
    });
    const Ghent = Palette({
      id: PaletteIds.GhentFive,
      label: 'reporting-snowflake.colors.palettes.ghent-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#5885C9',
          value: '#5885C9'
        }, {
          label: '',
          key: '#F2E2D2',
          value: '#F2E2D2'
        }, {
          label: '',
          key: '#C46B85',
          value: '#C46B85'
        }, {
          label: '',
          key: '#FF8B62',
          value: '#FF8B62'
        }, {
          label: '',
          key: '#63836E',
          value: '#63836E'
        }]
      })
    });
    const Ocean = Palette({
      id: PaletteIds.OceanFive,
      label: 'reporting-snowflake.colors.palettes.ocean-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#00155D',
          value: '#00155D'
        }, {
          label: '',
          key: '#005C87',
          value: '#005C87'
        }, {
          label: '',
          key: '#008C9B',
          value: '#008C9B'
        }, {
          label: '',
          key: '#88DFD7',
          value: '#88DFD7'
        }, {
          label: '',
          key: '#CCF2F0',
          value: '#CCF2F0'
        }]
      })
    });
    const Spring = Palette({
      id: PaletteIds.SpringFive,
      label: 'reporting-snowflake.colors.palettes.spring-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#C44170',
          value: '#C44170'
        }, {
          label: '',
          key: '#F7E0E0',
          value: '#F7E0E0'
        }, {
          label: '',
          key: '#6AACD0',
          value: '#6AACD0'
        }, {
          label: '',
          key: '#A0BC8E',
          value: '#A0BC8E'
        }, {
          label: '',
          key: '#B67D40',
          value: '#B67D40'
        }]
      })
    });
    const CherryBlossom = Palette({
      id: PaletteIds.CherryBlossomFive,
      label: 'reporting-snowflake.colors.palettes.cherry-blossom-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#D2B8C3',
          value: '#D2B8C3'
        }, {
          label: '',
          key: '#8D3E37',
          value: '#8D3E37'
        }, {
          label: '',
          key: '#B6674F',
          value: '#B6674F'
        }, {
          label: '',
          key: '#E8BAAF',
          value: '#E8BAAF'
        }, {
          label: '',
          key: '#845C76',
          value: '#845C76'
        }]
      })
    });
    const Pastel = Palette({
      id: PaletteIds.PastelFive,
      label: 'reporting-snowflake.colors.palettes.pastel-five',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: '#EFF1DB',
          value: '#EFF1DB'
        }, {
          label: '',
          key: '#FFD4DB',
          value: '#FFD4DB'
        }, {
          label: '',
          key: '#D3B5E5',
          value: '#D3B5E5'
        }, {
          label: '',
          key: '#B4F8C8',
          value: '#B4F8C8'
        }, {
          label: '',
          key: '#FFE4F8',
          value: '#FFE4F8'
        }]
      })
    });

    // DefaultY2 palette for Y2 axis fallback
    const DefaultY2 = Palette({
      id: PaletteIds.DefaultY2,
      label: 'reporting-snowflake.colors.palettes.default-y2',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: getTrellisTokenHexValue('fill-accent-neutral-default'),
          value: getTrellisTokenHexValue('fill-accent-neutral-default')
        }]
      })
    });

    // Alert palettes for GAUGE_PALETTES
    const AlertRedToGreen = Palette({
      id: PaletteIds.AlertRedToGreen,
      label: 'reporting-snowflake.colors.palettes.alertRedToGreen',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-alert'),
          value: getTrellisTokenHexValue('reporting-fill-chart-alert')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-default-light-pink'),
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-pink')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-caution'),
          value: getTrellisTokenHexValue('reporting-fill-chart-caution')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-default-light-green'),
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-green')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-positive'),
          value: getTrellisTokenHexValue('reporting-fill-chart-positive')
        }]
      })
    });
    const AlertGreenToRed = Palette({
      id: PaletteIds.AlertGreenToRed,
      label: 'reporting-snowflake.colors.palettes.alertGreenToRed',
      scheme: DiscreteScheme({
        colors: [{
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-positive'),
          value: getTrellisTokenHexValue('reporting-fill-chart-positive')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-default-light-green'),
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-green')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-caution'),
          value: getTrellisTokenHexValue('reporting-fill-chart-caution')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-default-light-pink'),
          value: getTrellisTokenHexValue('reporting-fill-chart-default-light-pink')
        }, {
          label: '',
          key: getTrellisTokenHexValue('reporting-fill-chart-alert'),
          value: getTrellisTokenHexValue('reporting-fill-chart-alert')
        }]
      })
    });

    // Return all palettes as ordered map
    const DEFAULT_PALETTES = List.of(DefaultCategorical, PatternFill14, ShadesOfOrange, ShadesOfAqua, ShadesOfPurple, ShadesOfYellow, ShadesOfPink, ShadesOfBlue, ShadesOfGreen, Tokyo, Bogota, Dublin, Cambridge, Singapore, Sydney, Berlin, Paris, SanFrancisco, Portsmouth, Ghent, Ocean, Spring, CherryBlossom, Pastel).toOrderedMap().mapKeys((_, v) => v && v.id);
    const GAUGE_PALETTES = List.of(DefaultCategorical, AlertRedToGreen, AlertGreenToRed, ShadesOfOrange, ShadesOfAqua, ShadesOfPurple, ShadesOfYellow, ShadesOfPink, ShadesOfBlue, ShadesOfGreen, Tokyo, Bogota, Dublin, Cambridge, Singapore, Sydney, Berlin, Paris, SanFrancisco, Portsmouth, Ghent, Ocean, Spring, CherryBlossom, Pastel).toOrderedMap().mapKeys((_, v) => v && v.id);
    return {
      // Individual palettes (maintaining same API as original palettes.ts)
      DefaultCategorical,
      PatternFill14,
      ShadesOfAqua,
      ShadesOfOrange,
      ShadesOfPurple,
      ShadesOfYellow,
      ShadesOfPink,
      ShadesOfBlue,
      ShadesOfGreen,
      Tokyo,
      Bogota,
      Dublin,
      Cambridge,
      Singapore,
      Sydney,
      Berlin,
      Paris,
      SanFrancisco,
      Portsmouth,
      Ghent,
      Ocean,
      Spring,
      CherryBlossom,
      Pastel,
      DefaultY2,
      AlertRedToGreen,
      AlertGreenToRed,
      // Palette collections
      DEFAULT_PALETTES,
      GAUGE_PALETTES
    };
  }, [theme, gates]);
};